import React from "react";
import { cx } from "utils/join-classnames";
import { Icon, Tooltip } from "ui/components/";
import { Segment } from "core/models";
import { getFileURL, segmentTimeDisplayString } from "core/models/Segment";
import styles from "./ChunkStyles.module.scss";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    segment: Segment;
    channelID: number;
    showDuration: boolean;
    showTimestamp: boolean;
    isPlaying: boolean;

    isSelectionStart: boolean;
    isSelectionEnd: boolean;
    selected: boolean;
    suspence?: boolean;
    position: "in" | "before" | "after";

    // Click handlers for the left and right halfs of a chunk
    onClickLeft(segmentID: string): void;
    onClickRight(segmentID: string): void;
}

const getTooltipText = (
    side: "left" | "right",
    position: "in" | "before" | "after",
): string => {
    let action = "Select";
    let preposition = "as";
    if (
        (side === "left" && position === "after") ||
        (side === "right" && position === "before")
    ) {
        action = "Reset";
    }
    if (
        (side === "left" && position === "before") ||
        (side === "right" && position === "after")
    ) {
        action = "Change";
        preposition = "to";
    }
    return `${action} ${preposition} ${side === "left" ? "IN" : "OUT"}`;
};

const Chunk: React.FC<Props> = ({
    segment,
    channelID,
    showDuration,
    showTimestamp,
    isPlaying,
    isSelectionStart,
    isSelectionEnd,
    selected,
    onClickLeft,
    onClickRight,
    className,
    suspence,
    position,
}) => {
    const chunkClasses = cx(
        styles.chunk__item,
        className,
        selected ? styles.selected : null,
        isPlaying ? styles.playing : null,
        isSelectionStart ? styles.start__selection : null,
        isSelectionEnd ? styles.end__selection : null,
    );
    return (
        <div className={chunkClasses}>
            <div
                className={styles.inner}
                style={
                    !suspence
                        ? {
                              backgroundImage: `url("${getFileURL(
                                  channelID,
                                  segment.jpgPath,
                                  segment.token,
                              )}")`,
                          }
                        : {}
                }
            >
                <Tooltip
                    text={getTooltipText("left", position)}
                    className={styles.tooltip}
                >
                    <div
                        className={styles.previous__half}
                        data-start-chunkid={segment.id}
                        onClick={() => onClickLeft(segment.id)}
                        role="presentation"
                    />
                </Tooltip>
                <Tooltip
                    text={getTooltipText("right", position)}
                    className={styles.tooltip}
                >
                    <div
                        className={styles.next__half}
                        data-end-chunkid={segment.id}
                        onClick={() => onClickRight(segment.id)}
                        role="presentation"
                    />
                </Tooltip>
                {(showTimestamp || showDuration) && (
                    <div className={styles.info}>
                        {showTimestamp && segmentTimeDisplayString(segment)}
                        {showTimestamp && showDuration && <br />}
                        {showDuration && `${segment.tsLength.toFixed(2)}`}
                    </div>
                )}
                {isPlaying && (
                    <div className={styles.playing__icon}>
                        <Icon name="pause" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(Chunk);
